import React, { Fragment, useState } from "react";
import { Formik, Form, Field } from "formik";
import { Button, useTheme, Typography, Stack } from "@mui/material";
import { object, string } from "yup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Loader from "../public/Loader";
import { getStyleForThankyouResponse } from "../styles/commonStyles";
import { CustomSelect } from "../../components/FormikValidatedComponents/CustomSelect";

const YEAR_COUNT = 50;
const DAYS_IN_MONTH = {
  default: 31,
  short: 30,
  february: 28,
  februaryLeap: 29,
};
const LEAP_YEAR_DIVISOR = 4;
const CENTURY_DIVISOR = 100;
const LEAP_CENTURY_DIVISOR = 400;
const FEBRUARY = 2;
const APRIL = 4;
const JUNE = 6;
const SEPTEMBER = 9;
const NOVEMBER = 11;
const SHORT_MONTHS = new Set([APRIL, JUNE, SEPTEMBER, NOVEMBER]);

const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const getYearOptions = (limitToCurrentAndNext = false) => {
  const currentYear = new Date().getFullYear();
  if (limitToCurrentAndNext) {
    return [
      { value: currentYear, label: currentYear },
      { value: currentYear + 1, label: currentYear + 1 },
      { value: currentYear + 2, label: currentYear + 2 },
    ];
  }
  return Array.from({ length: YEAR_COUNT }, (_, i) => ({
    value: currentYear - i,
    label: currentYear - i,
  }));
};

const getDaysInMonth = (month, year) => {
  if (!month) {
    return DAYS_IN_MONTH.default;
  }
  const monthNum = Number.parseInt(month, 10);
  const yearNum = Number.parseInt(year, 10) || new Date().getFullYear();
  if (SHORT_MONTHS.has(monthNum)) {
    return DAYS_IN_MONTH.short;
  }
  if (monthNum === FEBRUARY) {
    const isLeapYear =
      (yearNum % LEAP_YEAR_DIVISOR === 0 && yearNum % CENTURY_DIVISOR !== 0) ||
      yearNum % LEAP_CENTURY_DIVISOR === 0;
    return isLeapYear ? DAYS_IN_MONTH.februaryLeap : DAYS_IN_MONTH.february;
  }
  return DAYS_IN_MONTH.default;
};

const getDayOptions = (month, year) => {
  const daysInMonth = getDaysInMonth(month, year);
  return Array.from({ length: daysInMonth }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: i + 1,
  }));
};

const ScreeningQuestionTwo = ({
  questionText,
  backRoute,
  nextRoute,
  onSubmitHandler,
  showDayPicker = false,
}) => {
  const theme = useTheme();
  const classes = getStyleForThankyouResponse(theme);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const yearOptions = React.useMemo(
    () => getYearOptions(showDayPicker),
    [showDayPicker]
  );

  const handleSubmit = async (values) => {
    const year = values.year;
    const month = values.month || "01";
    const day = showDayPicker ? values.day || "01" : "01";
    const date = `${year}-${month}-${day}`;
    const selectedOption = location.state["selectedOption"];
    const payload = {
      [`${selectedOption}`]: {
        note: null,
        date,
      },
    };
    const details = { selectedOption, date };
    setLoading(true);
    const result = await onSubmitHandler(payload);
    setLoading(false);
    if (result) {
      history.push({ pathname: nextRoute, state: details });
    }
  };

  const initialValues = showDayPicker
    ? { year: "", month: "", day: "" }
    : { year: "", month: "" };

  const validationSchema = showDayPicker
    ? object({
        year: string().required(),
        month: string(),
        day: string(),
      })
    : object({
        year: string().required(),
        month: string(),
      });

  return (
    <Fragment>
      {loading && <Loader />}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <Stack sx={classes.wrapper} gap={"24px"} alignItems="start">
              <Button
                sx={classes.back}
                onClick={() => {
                  history.push(backRoute);
                }}
              >
                <ArrowBackIcon /> &nbsp;Back
              </Button>
              <Typography sx={classes.subTitle2}>{questionText}</Typography>
              <Field
                name="year"
                as={CustomSelect}
                label="Year"
                options={yearOptions}
                onChange={(e) => {
                  const newYear = e.target.value;
                  formikProps.setFieldValue("year", newYear);
                  if (showDayPicker) {
                    const maxDays = getDaysInMonth(
                      formikProps.values.month,
                      newYear
                    );
                    if (
                      formikProps.values.day &&
                      Number.parseInt(formikProps.values.day, 10) > maxDays
                    ) {
                      formikProps.setFieldValue("day", "");
                    }
                  }
                }}
              />
              <Field
                name="month"
                as={CustomSelect}
                label={`Month ${!showDayPicker ? "(Optional)" : ""}`}
                options={MONTH_OPTIONS}
                onChange={(e) => {
                  const newMonth = e.target.value;
                  formikProps.setFieldValue("month", newMonth);
                  if (showDayPicker) {
                    const maxDays = getDaysInMonth(
                      newMonth,
                      formikProps.values.year
                    );
                    if (
                      formikProps.values.day &&
                      Number.parseInt(formikProps.values.day, 10) > maxDays
                    ) {
                      formikProps.setFieldValue("day", "");
                    }
                  }
                }}
              />
              {showDayPicker && (
                <Field
                  name="day"
                  as={CustomSelect}
                  label="Day (Optional)"
                  options={getDayOptions(
                    formikProps.values.month,
                    formikProps.values.year
                  )}
                />
              )}
              <Button
                type="submit"
                sx={classes.btnStyle}
                disabled={loading || !!formikProps.errors.year}
              >
                Next
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    </Fragment>
  );
};

ScreeningQuestionTwo.propTypes = {
  questionText: PropTypes.string.isRequired,
  backRoute: PropTypes.string.isRequired,
  nextRoute: PropTypes.string.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  showDayPicker: PropTypes.bool,
};

ScreeningQuestionTwo.defaultProps = {
  showDayPicker: false,
};

export default ScreeningQuestionTwo;
